import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, GraduationCap, Users, PlusCircle, ArrowLeft, LayoutDashboard, ShieldCheck, Megaphone, Clock, PanelLeftClose, Video } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import SessionAnalyticsModal from '../components/SessionAnalyticsModal';

const statusStyle = {
  verified: { bg: 'rgba(16,185,129,0.1)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' },
  in_progress: { bg: 'rgba(249,115,22,0.1)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.3)' },
  unverified: { bg: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' },
};

function AdminCard({ children }) {
  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(147, 51, 234, 0.12)', borderRadius: '1rem', padding: '1.25rem', boxShadow: '0 4px 20px rgba(147, 51, 234, 0.04)', transition: 'all 0.2s' }}
      onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.35)'}
      onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.12)'}
    >{children}</div>
  );
}

function ScholarshipsTab({ showAlert }) {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', provider: '', description: '', start_date: '', end_date: '', link: '', status: 'unverified', amount: '' });

  const load = useCallback(async () => {
    const data = await api.getCollection('scholarships').catch(() => []);
    setScholarships(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleVerify = async (id, status) => {
    try { await api.updateDocument('scholarships', id, { status }); await load(); showAlert('Updated', `Status changed to ${status}`); }
    catch { showAlert('Error', 'Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scholarship?')) return;
    try { await api.deleteDocument('scholarships', id); await load(); showAlert('Deleted', 'Scholarship removed'); }
    catch { showAlert('Error', 'Failed to delete'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.provider) { showAlert('Validation Error', 'Name and Provider are required'); return; }
    try {
      await api.createDocument('scholarships', form);
      setForm({ name: '', provider: '', description: '', start_date: '', end_date: '', link: '', status: 'unverified', amount: '' });
      await load();
      showAlert('Added!', 'Scholarship added successfully');
    } catch { showAlert('Error', 'Failed to add scholarship'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(147, 51, 234, 0.12)', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 4px 20px rgba(147, 51, 234, 0.04)' }}>
        <h4 style={{ fontWeight: 700, color: 'var(--primary-gold)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1.25rem' }}>+ Add New Scholarship</h4>
        <form onSubmit={handleSubmit}>
          <div className="admin-form-grid">
            {[
              { id: 'name', label: 'Scholarship Name *', placeholder: 'e.g. Science & Tech Grant' },
              { id: 'provider', label: 'Provider / Organisation *', placeholder: 'e.g. ISRO' },
              { id: 'amount', label: 'Amount (₹)', placeholder: 'e.g. 50000' },
              { id: 'link', label: 'Apply Link', placeholder: 'https://...' },
            ].map(f => (
              <div key={f.id}>
                <label className="admin-label">{f.label}</label>
                <input type={f.id === 'link' ? 'url' : 'text'} placeholder={f.placeholder} className="admin-input" value={form[f.id]} onChange={e => setForm(p => ({ ...p, [f.id]: e.target.value }))} />
              </div>
            ))}
            <div>
              <label className="admin-label">Start Date</label>
              <input type="date" className="admin-input" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div>
              <label className="admin-label">End Date</label>
              <input type="date" className="admin-input" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
            </div>
            <div>
              <label className="admin-label">Status</label>
              <select className="admin-input" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                <option value="unverified">Unverified</option>
                <option value="in_progress">In Progress</option>
                <option value="verified">Verified</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label className="admin-label">Description *</label>
            <textarea rows={3} placeholder="Brief description..." className="admin-input" style={{ resize: 'none' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <button type="submit" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg,var(--primary-gold),var(--primary-gold-deep))', color: 'white', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}>
            <PlusCircle size={16} /> Add Scholarship
          </button>
        </form>
      </div>
 
      {loading ? <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.875rem' }}>Loading...</p> :
        scholarships.length === 0 ? <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.875rem' }}>No scholarships yet.</p> :
          scholarships.map(s => {
            const st = statusStyle[s.status] || statusStyle.unverified;
            return (
              <AdminCard key={s.id}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div>
                    <h4 style={{ fontWeight: 700, color: 'var(--navy-dark)', fontSize: '0.875rem' }}>{s.name}</h4>
                    <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.125rem' }}>{s.provider || '—'}</p>
                  </div>
                  <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.25rem 0.625rem', borderRadius: '9999px', background: st.bg, color: st.color, border: st.border, whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{s.status}</span>
                </div>
                {s.description && <p style={{ fontSize: '0.8rem', color: 'var(--text-slate)', marginBottom: '1rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.description}</p>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {['verified', 'in_progress', 'unverified'].map(status => {
                    const colorMap = {
                      verified: { bg: 'rgba(16,185,129,0.08)', color: '#10b981', borderColor: 'rgba(16,185,129,0.2)' },
                      in_progress: { bg: 'rgba(249,115,22,0.08)', color: '#fb923c', borderColor: 'rgba(249,115,22,0.2)' },
                      unverified: { bg: 'rgba(239,68,68,0.08)', color: '#ef4444', borderColor: 'rgba(239,68,68,0.2)' },
                    };
                    const c = colorMap[status];
                    return (
                      <button key={status} onClick={() => handleVerify(s.id, status)} style={{ padding: '0.375rem 0.75rem', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderRadius: '0.5rem', background: c.bg, color: c.color, border: `1px solid ${c.borderColor}`, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
                        {status === 'verified' ? '✓ Verify' : status === 'in_progress' ? '⏳ In Progress' : '✗ Unverify'}
                      </button>
                    );
                  })}
                  <button onClick={() => handleDelete(s.id)} style={{ marginLeft: 'auto', padding: '0.375rem 0.75rem', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', borderRadius: '0.5rem', background: '#f1f5f9', color: '#64748b', border: '1px solid var(--slate-200)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
                    onMouseOver={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
                    onMouseOut={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = 'var(--slate-200)'; }}
                  >Delete</button>
                </div>
              </AdminCard>
            );
          })
      }
    </div>
  );
}

function UsersTab({ showAlert }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.adminListUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const filtered = search ? users.filter(u => u.full_name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())) : users;

  const changeRole = async (id, role) => {
    try {
      await api.adminUpdateUserRole(id, role);
      setUsers(u => u.map(x => x.id === id ? { ...x, role } : x));
      showAlert('Role Updated', `User role changed to ${role}`);
    } catch (e) {
      showAlert('Error', e?.message || 'Failed to update role');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <input type="text" placeholder="Search by name or email..." className="admin-input" style={{ maxWidth: '300px' }} value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>{filtered.length} users</span>
      </div>
      {filtered.map(u => (
        <AdminCard key={u.id}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${u.id}`} style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', border: '1px solid rgba(147, 51, 234, 0.12)', background: 'rgba(147, 51, 234, 0.04)' }} alt="" />
              <div>
                <p style={{ fontWeight: 700, color: 'var(--navy-dark)', fontSize: '0.875rem' }}>{u.full_name || '—'}</p>
                <p style={{ fontSize: '0.7rem', color: '#64748b' }}>{u.email}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary-gold)' }}>⚡ {u.score || 0}</p>
                <p style={{ fontSize: '0.7rem', color: '#64748b' }}>🔥 {u.streak || 0} streak</p>
              </div>
              <select value={u.role || 'student'} onChange={e => changeRole(u.id, e.target.value)} style={{ background: '#ffffff', border: '1px solid var(--slate-200)', color: 'var(--navy-dark)', borderRadius: '0.5rem', padding: '0.375rem 0.625rem', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', outline: 'none' }}>
                {['student', 'mentor', 'admin', 'parent'].map(r => <option key={r} value={r} style={{ background: '#ffffff' }}>{r}</option>)}
              </select>
            </div>
          </div>
        </AdminCard>
      ))}
    </div>
  );
}

function OverviewTab({ users, pendingMentorsCount }) {
  const roleCounts = users.reduce((acc, u) => { acc[u.role || 'student'] = (acc[u.role || 'student'] || 0) + 1; return acc; }, {});
  const cards = [
    { label: 'Total Users', value: users.length, icon: Users, color: '#2563eb' },
    { label: 'Students', value: roleCounts.student || 0, icon: GraduationCap, color: '#10b981' },
    { label: 'Mentors', value: roleCounts.mentor || 0, icon: ShieldCheck, color: '#8b5cf6' },
    { label: 'Pending Mentor Approvals', value: pendingMentorsCount, icon: Clock, color: '#d97706' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: '1rem' }}>
      {cards.map(({ label, value, icon: Icon, color }) => (
        <div key={label} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.05)', borderRadius: '1rem', padding: '1.25rem' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <Icon size={17} color={color} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy-dark)' }}>{value}</div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function MentorsTab({ showAlert, onChanged }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const data = await api.adminListPendingMentors().catch(() => []);
    setPending(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleApprove = async (userId) => {
    try { await api.adminApproveMentor(userId); await load(); onChanged?.(); showAlert('Approved', 'Mentor can now host sessions.'); }
    catch { showAlert('Error', 'Failed to approve mentor.'); }
  };
  const handleReject = async (userId) => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try { await api.adminRejectMentor(userId, reason); await load(); onChanged?.(); showAlert('Rejected', 'Mentor application declined.'); }
    catch { showAlert('Error', 'Failed to reject mentor.'); }
  };

  if (loading) return <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.875rem' }}>Loading...</p>;
  if (pending.length === 0) return <div style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', border: '1px dashed #e2e8f0', borderRadius: '1rem' }}>No pending mentor applications.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {pending.map((p) => (
        <AdminCard key={p.id}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${p.user?.id || p.id}`} alt="" style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid #e2e8f0' }} />
              <div>
                <p style={{ fontWeight: 700, color: 'var(--navy-dark)', fontSize: '0.875rem' }}>{p.user?.full_name || 'Unknown'}</p>
                <p style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.user?.email}</p>
                <p style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>{p.qualification || 'No qualification listed'}</p>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  {(p.subjects || []).map((s) => (
                    <span key={s} style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', background: 'rgba(139,92,246,.08)', border: '1px solid rgba(139,92,246,.2)', borderRadius: 20, padding: '2px 8px' }}>{s}</span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleApprove(p.userId || p.user?.id)} style={{ padding: '0.5rem 1rem', fontSize: '0.72rem', fontWeight: 700, borderRadius: '0.5rem', background: 'rgba(16,185,129,0.08)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)', cursor: 'pointer' }}>Approve</button>
              <button onClick={() => handleReject(p.userId || p.user?.id)} style={{ padding: '0.5rem 1rem', fontSize: '0.72rem', fontWeight: 700, borderRadius: '0.5rem', background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', cursor: 'pointer' }}>Reject</button>
            </div>
          </div>
        </AdminCard>
      ))}
    </div>
  );
}

function AnnouncementsTab({ showAlert }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', body: '', audience: 'all', severity: 'info' });

  const load = useCallback(async () => {
    const data = await api.adminListAnnouncements().catch(() => []);
    setAnnouncements(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { showAlert('Validation Error', 'Title and message are required'); return; }
    try {
      await api.adminCreateAnnouncement(form);
      setForm({ title: '', body: '', audience: 'all', severity: 'info' });
      await load();
      showAlert('Sent!', 'Announcement is now live for its audience.');
    } catch { showAlert('Error', 'Failed to send announcement'); }
  };

  const toggleActive = async (a) => {
    try { await api.adminUpdateAnnouncement(a.id, { active: !a.active }); await load(); }
    catch { showAlert('Error', 'Failed to update announcement'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.05)', borderRadius: '1rem', padding: '1.5rem' }}>
        <h4 style={{ fontWeight: 700, color: 'var(--navy-dark)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1.25rem' }}>Send a New Alert</h4>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          <div><label className="admin-label">Title *</label><input className="admin-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Platform maintenance tonight" /></div>
          <div><label className="admin-label">Message *</label><textarea rows={3} className="admin-input" style={{ resize: 'none' }} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} /></div>
          <div className="admin-form-grid" style={{ marginBottom: 0 }}>
            <div>
              <label className="admin-label">Audience</label>
              <select className="admin-input" value={form.audience} onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}>
                <option value="all">Everyone</option>
                <option value="students">All Students</option>
                <option value="mentors">All Mentors</option>
                <option value="school">School-tier Students</option>
                <option value="intermediate">Intermediate-tier Students</option>
                <option value="btech">B.Tech Students</option>
              </select>
            </div>
            <div>
              <label className="admin-label">Severity</label>
              <select className="admin-input" value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <button type="submit" style={{ alignSelf: 'flex-start', padding: '0.7rem 1.5rem', background: 'var(--navy-dark)', color: '#fff', borderRadius: '0.6rem', fontWeight: 700, fontSize: '0.8rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            Send Announcement
          </button>
        </form>
      </div>

      {loading ? <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.875rem' }}>Loading...</p> :
        announcements.map((a) => (
          <AdminCard key={a.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h4 style={{ fontWeight: 700, color: 'var(--navy-dark)', fontSize: '0.875rem' }}>{a.title}</h4>
                  {!a.active && <span style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', background: '#f1f5f9', borderRadius: 6, padding: '1px 6px' }}>INACTIVE</span>}
                </div>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>{a.body}</p>
                <p style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 6, textTransform: 'capitalize' }}>{a.audience} · {a.severity} · {new Date(a.createdAt).toLocaleDateString()}</p>
              </div>
              <button onClick={() => toggleActive(a)} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.4rem 0.8rem', borderRadius: '0.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {a.active ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </AdminCard>
        ))}
    </div>
  );
}

function RoomsTab({ showAlert }) {
  const [subTab, setSubTab] = useState('peer'); // 'peer' | 'mentor'
  const [peerSessions, setPeerSessions] = useState([]);
  const [mentorSessions, setMentorSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsFor, setAnalyticsFor] = useState(null); // { id, type }

  const load = useCallback(async () => {
    setLoading(true);
    const [peer, mentor] = await Promise.all([
      api.adminListAllPeerSessions().catch(() => []),
      api.adminListAllMentorSessions().catch(() => []),
    ]);
    setPeerSessions(peer);
    setMentorSessions(mentor);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (type, id) => {
    if (!window.confirm('Permanently delete this room/session? This cannot be undone.')) return;
    try {
      if (type === 'peer') await api.adminDeletePeerSession(id);
      else await api.adminDeleteMentorSession(id);
      await load();
      showAlert('Deleted', 'The room has been removed.');
    } catch {
      showAlert('Error', 'Failed to delete.');
    }
  };

  const rows = subTab === 'peer' ? peerSessions : mentorSessions;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {[{ v: 'peer', label: 'Peer Rooms' }, { v: 'mentor', label: 'Mentoring Sessions' }].map((t) => (
          <button key={t.v} onClick={() => setSubTab(t.v)}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.6rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
              border: subTab === t.v ? '1px solid var(--primary-gold)' : '1px solid #e2e8f0',
              background: subTab === t.v ? 'rgba(147,51,234,.06)' : '#fff',
              color: subTab === t.v ? 'var(--primary-gold)' : '#64748b',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.875rem' }}>Loading...</p> :
        rows.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', border: '1px dashed #e2e8f0', borderRadius: '1rem' }}>No {subTab === 'peer' ? 'peer rooms' : 'mentoring sessions'} yet.</div>
        ) : rows.map((s) => (
          <AdminCard key={s.id}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <p style={{ fontWeight: 700, color: 'var(--navy-dark)', fontSize: '0.875rem' }}>{s.title}</p>
                  <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', borderRadius: 6, padding: '1px 6px', background: s.status === 'live' ? 'rgba(16,185,129,.1)' : s.status === 'ended' ? '#f1f5f9' : 'rgba(37,99,235,.1)', color: s.status === 'live' ? '#10b981' : s.status === 'ended' ? '#94a3b8' : '#2563eb' }}>{s.status}</span>
                  {s.isDefaultRoom && <span style={{ fontSize: 9, fontWeight: 800, color: '#2563eb', background: 'rgba(37,99,235,.08)', borderRadius: 6, padding: '1px 6px' }}>STANDING ROOM</span>}
                </div>
                <p style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>
                  Host: {s.host_name || s.mentor_name || 'Ekalavya'} · {s.enrolled_count}/{s.total_capacity} joined
                </p>
                {s.meetingLink && <p style={{ fontSize: '0.7rem', color: '#8b5cf6', marginTop: 2, wordBreak: 'break-all' }}>{s.meetingLink}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => setAnalyticsFor({ id: s.id, type: subTab })}
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem', fontWeight: 700, borderRadius: '0.5rem', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', cursor: 'pointer' }}>
                  Analytics
                </button>
                <button onClick={() => handleDelete(subTab, s.id)}
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem', fontWeight: 700, borderRadius: '0.5rem', background: 'rgba(239,68,68,.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,.2)', cursor: 'pointer' }}>
                  Delete
                </button>
              </div>
            </div>
          </AdminCard>
        ))}

      {analyticsFor && <SessionAnalyticsModal sessionId={analyticsFor.id} type={analyticsFor.type} onClose={() => setAnalyticsFor(null)} />}
    </div>
  );
}

const adminTabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'scholarships', label: 'Content', icon: GraduationCap },
  { id: 'mentors', label: 'Mentor Approvals', icon: ShieldCheck },
  { id: 'rooms', label: 'Rooms', icon: Video },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
];

export default function Admin() {
  const { user, profile, loading: authLoading } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingMentorsCount, setPendingMentorsCount] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    if (profile && profile.role !== 'admin') {
      navigate('/');
      showAlert('Access Denied', 'Admin access required.');
    }
  }, [user, profile, authLoading, navigate, showAlert]);

  const refreshOverview = useCallback(() => {
    api.adminListUsers().then(setUsers).catch(() => setUsers([]));
    api.adminListPendingMentors().then(p => setPendingMentorsCount(p.length)).catch(() => setPendingMentorsCount(0));
  }, []);
  useEffect(() => { refreshOverview(); }, [refreshOverview]);

  if (authLoading) {
    return <div style={{ padding: '6rem 2rem', textAlign: 'center', color: '#64748b' }}>Loading admin panel...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ maxWidth: '1200px', paddingTop: '2rem', paddingBottom: '3rem' }}>
        <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#64748b', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', marginBottom: '1.5rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', fontFamily: 'inherit', marginLeft: '-1rem' }} onMouseOver={e => { e.currentTarget.style.background = '#ffffff'; }} onMouseOut={e => { e.currentTarget.style.background = 'none'; }}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{ width: '3rem', height: '3rem', background: 'var(--navy-dark)', borderRadius: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--navy-dark)', letterSpacing: '-0.01em' }}>Admin Panel</h1>
            <p style={{ color: '#64748b', fontSize: '0.82rem' }}>Manage users, content, mentors, and platform-wide announcements</p>
          </div>
        </div>

        <div className="admin-shell" style={{ gridTemplateColumns: navCollapsed ? '64px 1fr' : '220px 1fr', transition: 'grid-template-columns 0.2s ease' }}>
          <nav className="admin-shell-nav">
            <button
              onClick={() => setNavCollapsed((c) => !c)}
              className="admin-shell-collapse-btn"
              style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.9rem', borderRadius: '0.6rem', background: 'transparent', border: '1px dashed #e2e8f0', color: '#94a3b8', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '0.25rem' }}
            >
              <PanelLeftClose size={14} style={{ transform: navCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              {!navCollapsed && 'Collapse'}
            </button>
            {adminTabs.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setActiveTab(id)} title={navCollapsed ? label : ''}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 0.9rem', borderRadius: '0.6rem',
                  background: activeTab === id ? '#ffffff' : 'transparent',
                  color: activeTab === id ? 'var(--navy-dark)' : '#64748b',
                  border: activeTab === id ? '1px solid rgba(0,0,0,0.06)' : '1px solid transparent',
                  boxShadow: activeTab === id ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                  fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', textAlign: 'left',
                  transition: 'all 0.18s ease',
                }}
              >
                <Icon size={16} />{!navCollapsed && label}
              </button>
            ))}
          </nav>

          <div>
            {activeTab === 'overview' && <OverviewTab users={users} pendingMentorsCount={pendingMentorsCount} />}
            {activeTab === 'scholarships' && <ScholarshipsTab showAlert={showAlert} />}
            {activeTab === 'users' && <UsersTab showAlert={showAlert} />}
            {activeTab === 'mentors' && <MentorsTab showAlert={showAlert} onChanged={refreshOverview} />}
            {activeTab === 'rooms' && <RoomsTab showAlert={showAlert} />}
            {activeTab === 'announcements' && <AnnouncementsTab showAlert={showAlert} />}
          </div>
        </div>
      </div>
    </div>
  );
}
